"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Flame, Users, Star, Package, Heart, BookOpen, Loader2, Sparkles,
  ShieldCheck, ShieldX, CreditCard, Info, ExternalLink, FileText,
  Video, Rss, Newspaper,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

// ── Types ─────────────────────────────────────────────────────────────────────

interface MembershipDetails {
  status?: string | number;
  is_active?: boolean;
  expiry_date?: number;
  plan_name?: string;
  package_name?: string;
  subscription_id?: string;
  next_billing_date?: number;
  email?: string;
  amount?: number | string;
  created_on?: number;
}

interface ChartData {
  chart_image?: string;
  interpretation?: string;
  chart_url?: string;
  [key: string]: unknown;
}

interface MemberData {
  _id?: string;
  name?: string;
  firstname?: string;
  lastname?: string;
  sub_relation?: string;
  completed_main_user?: number;
  is_others?: { other_percentage?: number };
  [key: string]: unknown;
}

interface BlogItem {
  _id: string;
  title?: string;
  blog_title?: string;
  created_on?: number;
  thumbnail_path?: { baseurl?: string; fileservername?: string };
}

interface VideoItem {
  _id: string;
  title?: string;
  video_title?: string;
  created_on?: number;
  thumbnail_path?: { baseurl?: string; fileservername?: string };
  videotype?: string;
  video_url?: string;
}

interface ContentItem {
  _id: string;
  title?: string;
  content_type?: string;
  priority?: number;
  created_on?: number;
}

interface ContentCounts {
  live_stream?: number;
  video_library?: number;
  document?: number;
  youtube?: number;
  [key: string]: unknown;
}

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";
}

// ── SVG Progress Ring ─────────────────────────────────────────────────────────

function ProgressRing({
  pct,
  size = 80,
  stroke = 7,
  color = "#ffa800",
}: {
  pct: number;
  size?: number;
  stroke?: number;
  color?: string;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(240 20% 20%)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
    </svg>
  );
}

// ── Subscription Status Widget ────────────────────────────────────────────────

function SubscriptionWidget({ membership, isLoading, onSubscribe, subscribing }: {
  membership: MembershipDetails | null;
  isLoading: boolean;
  onSubscribe: () => void;
  subscribing: boolean;
}) {
  const isActive =
    membership?.is_active === true ||
    membership?.status === "active" ||
    membership?.status === 1;

  if (isLoading) {
    return (
      <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
        <CardContent className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Checking subscription...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ borderColor: isActive ? "rgba(22,160,133,0.4)" : "rgba(233,74,73,0.3)", background: isActive ? "rgba(22,160,133,0.05)" : "rgba(233,74,73,0.04)" }}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {isActive
              ? <ShieldCheck className="size-8 shrink-0" style={{ color: "#16a085" }} />
              : <ShieldX className="size-8 shrink-0" style={{ color: "#e94a49" }} />}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base font-semibold text-foreground">Perennial Mandalism Subscription</p>
                <Badge className="border text-[10px]" style={
                  isActive
                    ? { background: "rgba(22,160,133,0.15)", color: "#16a085", borderColor: "rgba(22,160,133,0.3)" }
                    : { background: "rgba(233,74,73,0.12)", color: "#e94a49", borderColor: "rgba(233,74,73,0.3)" }
                }>
                  {isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              {/* Task 05: show email, amount, created date, status detail */}
              {membership?.email && <p className="text-xs text-muted-foreground mt-0.5">{membership.email}</p>}
              {membership?.plan_name ?? membership?.package_name ? (
                <p className="text-xs text-muted-foreground mt-0.5">Plan: {membership.plan_name ?? membership.package_name}</p>
              ) : null}
              {membership?.amount != null && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  ${membership.amount}<span className="opacity-60">/month</span>
                </p>
              )}
              {isActive && membership?.expiry_date ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Renews: {format(new Date(membership.expiry_date), "MMMM dd, yyyy")}
                </p>
              ) : null}
              {membership?.created_on ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Member since: {format(new Date(membership.created_on), "MMMM dd, yyyy")}
                </p>
              ) : null}
              {!isActive && (
                <p className="text-xs text-muted-foreground mt-1">
                  Subscribe to access rituals, member benefits, and exclusive perennial content.
                </p>
              )}
            </div>
          </div>
          {!isActive && (
            <button
              type="button" disabled={subscribing} onClick={onSubscribe}
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
              style={{ background: "rgba(255,168,0,0.15)", border: "1px solid rgba(255,168,0,0.4)", color: "#ffa800" }}
            >
              {subscribing ? <Loader2 className="size-4 animate-spin" /> : <CreditCard className="size-4" />}
              Subscribe Now
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Quick Link Card ───────────────────────────────────────────────────────────

function QuickCard({ title, description, icon: Icon, href }: {
  title: string; description: string;
  icon: React.ElementType<{ className?: string }>; href: string;
}) {
  const router = useRouter();
  return (
    <Card className="cursor-pointer transition-colors" style={{ borderColor: "hsl(240 20% 22%)" }} onClick={() => router.push(href)}>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
        <div className="rounded-lg p-2" style={{ background: "rgba(255,168,0,0.12)" }}>
          <Icon className="size-4" style={{ color: "#ffa800" }} />
        </div>
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent><p className="text-xs text-muted-foreground">{description}</p></CardContent>
    </Card>
  );
}

// ── Task 01: Astro Chart Card ─────────────────────────────────────────────────

function AstroChartCard({
  title, tooltip, chartData, isPolling,
}: {
  title: string; tooltip: string;
  chartData: ChartData | null; isPolling: boolean;
}) {
  const [showTip, setShowTip] = useState(false);
  const imgSrc = chartData?.chart_image ?? chartData?.chart_url ?? "";

  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
          {title}
          <button type="button" className="relative ml-auto" onClick={() => setShowTip((v) => !v)}>
            <Info className="size-3.5 text-muted-foreground hover:text-foreground transition-colors" />
            {showTip && (
              <div
                className="absolute right-0 top-5 z-10 w-56 rounded-lg p-3 text-xs text-muted-foreground shadow-xl"
                style={{ background: "hsl(240 28% 14%)", border: "1px solid hsl(240 20% 22%)" }}
              >
                {tooltip}
              </div>
            )}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        {isPolling && !chartData ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-sm text-muted-foreground">
            <Loader2 className="size-6 animate-spin" style={{ color: "#ffa800" }} />
            <p>Your chart is preparing...</p>
            <p className="text-xs opacity-60">Checking every 10 seconds</p>
          </div>
        ) : imgSrc ? (
          <div className="space-y-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={imgSrc} alt={title} className="w-full rounded-lg object-contain max-h-64" />
            {chartData?.interpretation && (
              <p className="text-xs text-muted-foreground leading-relaxed">{chartData.interpretation as string}</p>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            Chart data unavailable.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Task 02: Profile Progress Rings ──────────────────────────────────────────

function ProfileRingsCard({ memberData, isLoading }: {
  memberData: MemberData | null; isLoading: boolean;
}) {
  const userPct = memberData?.completed_main_user ?? 0;
  const otherPct = memberData?.is_others?.other_percentage ?? 0;

  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
          Profile Completion
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : (
          <div className="flex gap-8 justify-center">
            {[
              { label: "Your Profile", pct: userPct, color: "#ffa800" },
              { label: "Members' Profiles", pct: otherPct, color: "#25aceb" },
            ].map(({ label, pct, color }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <div className="relative">
                  <ProgressRing pct={pct} color={color} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold" style={{ color }}>{Math.round(pct)}%</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center">{label}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Task 03: Relationship / Members List ──────────────────────────────────────

function RelationshipCard({ members, isLoading }: {
  members: MemberData[]; isLoading: boolean;
}) {
  const router = useRouter();
  // Angular: slice(1) — skip the first (main) user
  const others = members.slice(1);
  const connectedCount = others.length;

  function initials(m: MemberData) {
    const full = m.name ?? `${m.firstname ?? ""} ${m.lastname ?? ""}`.trim() ?? "?";
    return full.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  }

  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm">
            <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Relationships
            <Badge className="ml-1 text-[10px] border" style={{ background: "rgba(255,168,0,0.1)", color: "#ffa800", borderColor: "rgba(255,168,0,0.3)" }}>
              {connectedCount} Connected
            </Badge>
          </CardTitle>
          <button
            type="button"
            onClick={() => router.push("/admin/perennial/relationship-details")}
            className="text-[11px] hover:underline"
            style={{ color: "#25aceb" }}
          >
            Explore Compatibility →
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : others.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No other members to display.</p>
        ) : (
          <div className="space-y-3">
            {others.slice(0, 5).map((m, i) => {
              const fullName = (m.name ?? `${m.firstname ?? ""} ${m.lastname ?? ""}`.trim()).toUpperCase();
              return (
                <div key={m._id ?? i} className="flex items-center gap-3">
                  <div
                    className="shrink-0 size-9 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: "rgba(255,168,0,0.15)", color: "#ffa800", border: "1px solid rgba(255,168,0,0.3)" }}
                  >
                    {initials(m)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{fullName || "—"}</p>
                    {m.sub_relation && (
                      <p className="text-xs text-muted-foreground">{m.sub_relation}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {others.length > 5 && (
              <button
                type="button"
                onClick={() => router.push("/admin/perennial/members")}
                className="text-xs hover:underline"
                style={{ color: "#ffa800" }}
              >
                +{others.length - 5} more members →
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Task 04: Promo Blocks ─────────────────────────────────────────────────────

function PromoBlocks() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Donation block */}
      <Card style={{ borderColor: "rgba(255,168,0,0.25)", background: "rgba(255,168,0,0.03)" }}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Flame className="size-5 shrink-0" style={{ color: "#ffa800" }} />
            <h3 className="text-sm font-semibold text-foreground">Support the Eternal Flame</h3>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your generous contributions keep the sacred flame of Perennial Mandalism burning.
            Every donation directly supports the continuation of divine teachings, rituals, and
            the collective spiritual journey of our community.
          </p>
          <a
            href="/admin/packages"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
            style={{ background: "rgba(255,168,0,0.15)", border: "1px solid rgba(255,168,0,0.35)", color: "#ffa800" }}
          >
            <Heart className="size-3.5" /> Donate Now
          </a>
        </CardContent>
      </Card>

      {/* Mystery School block */}
      <Card style={{ borderColor: "rgba(124,58,237,0.3)", background: "rgba(124,58,237,0.04)" }}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Star className="size-5 shrink-0" style={{ color: "#7c3aed" }} />
            <h3 className="text-sm font-semibold text-foreground">Enter the Sacred Gateway</h3>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">2025 Grand Opening</span> — The Mystery School
            opens its doors to sincere seekers of the ancient wisdom. Join an exclusive community
            devoted to esoteric study and spiritual mastery for only{" "}
            <span className="font-bold" style={{ color: "#7c3aed" }}>$10/month</span>.
          </p>
          <a
            href="/admin/training/center"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
            style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.35)", color: "#7c3aed" }}
          >
            <BookOpen className="size-3.5" /> Register for the Mystery School
          </a>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Task 05: Content Counts Card ──────────────────────────────────────────────

function ContentCountsCard({ counts, isLoading }: {
  counts: ContentCounts | null; isLoading: boolean;
}) {
  const items = [
    { label: "Live Streams", key: "live_stream", icon: Rss, color: "#e94a49" },
    { label: "Video Library", key: "video_library", icon: Video, color: "#25aceb" },
    { label: "Documents", key: "document", icon: FileText, color: "#16a085" },
    { label: "YouTube", key: "youtube", icon: ExternalLink, color: "#ffa800" },
  ] as const;

  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
          Content Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {items.map(({ label, key, icon: Icon, color }) => (
              <div
                key={key}
                className="rounded-xl p-3 text-center space-y-1"
                style={{ background: `${color}10`, border: `1px solid ${color}25` }}
              >
                <Icon className="mx-auto size-4" style={{ color }} />
                <p className="text-xl font-bold" style={{ color }}>
                  {isLoading ? "…" : (counts?.[key] ?? 0)}
                </p>
                <p className="text-[10px] text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Task 06: Media Lists ──────────────────────────────────────────────────────

function BlogListCard({ blogs, isLoading }: { blogs: BlogItem[]; isLoading: boolean }) {
  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
          <Newspaper className="size-3.5" /> Recent Blogs
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : blogs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No blogs available.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: "hsl(240 20% 20%)" }}>
            {blogs.slice(0, 5).map((b) => (
              <div key={b._id} className="flex items-center gap-3 px-4 py-3">
                {(() => {
                  const thumb = b.thumbnail_path;
                  const src = thumb ? `${thumb.baseurl ?? ""}${thumb.fileservername ?? ""}` : "";
                  return src ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={src} alt="" className="size-9 rounded-md object-cover shrink-0" />
                  ) : (
                    <div className="size-9 rounded-md shrink-0 flex items-center justify-center" style={{ background: "hsl(240 20% 18%)" }}>
                      <Newspaper className="size-4 text-muted-foreground" />
                    </div>
                  );
                })()}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{b.blog_title ?? b.title ?? "—"}</p>
                  {b.created_on && (
                    <p className="text-xs text-muted-foreground">{format(new Date(b.created_on), "MMM dd, yyyy")}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VideoListCard({ videos, isLoading }: { videos: VideoItem[]; isLoading: boolean }) {
  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#25aceb" }} />
          <Video className="size-3.5" style={{ color: "#25aceb" }} /> Recent Videos
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No videos available.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: "hsl(240 20% 20%)" }}>
            {videos.slice(0, 5).map((v) => {
              const thumb = v.thumbnail_path;
              const src = thumb ? `${thumb.baseurl ?? ""}${thumb.fileservername ?? ""}` : "";
              return (
                <div key={v._id} className="flex items-center gap-3 px-4 py-3">
                  {src ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={src} alt="" className="size-9 rounded-md object-cover shrink-0" />
                  ) : (
                    <div className="size-9 rounded-md shrink-0 flex items-center justify-center" style={{ background: "hsl(240 20% 18%)" }}>
                      <Video className="size-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{v.video_title ?? v.title ?? "—"}</p>
                    {v.created_on && (
                      <p className="text-xs text-muted-foreground">{format(new Date(v.created_on), "MMM dd, yyyy")}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ContentTableCard({ items, isLoading }: { items: ContentItem[]; isLoading: boolean }) {
  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#16a085" }} />
          <FileText className="size-3.5" style={{ color: "#16a085" }} /> Dashboard Content
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No content items.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: "hsl(240 20% 20%)" }}>
            {items.slice(0, 8).map((c) => (
              <div key={c._id} className="flex items-center justify-between px-4 py-2.5">
                <p className="text-sm font-medium truncate">{c.title ?? "—"}</p>
                {c.content_type && (
                  <Badge className="ml-2 shrink-0 text-[10px] border" style={{ background: "rgba(22,160,133,0.1)", color: "#16a085", borderColor: "rgba(22,160,133,0.25)" }}>
                    {c.content_type}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PerennialDashboardPage() {
  const router = useRouter();
  const { data: session } = useSession();
  const baseUrl = getBaseUrl();

  // Session user IDs
  const cognitoId = session?.user?.cognito_user_id ?? "";
  const userId = session?.user?._id ?? cognitoId;

  // ── Task 01: Astro chart polling ──────────────────────────────────────────
  const [natalChart, setNatalChart] = useState<ChartData | null>(null);
  const [monthlyTransit, setMonthlyTransit] = useState<ChartData | null>(null);
  const [isPollingCharts, setIsPollingCharts] = useState(true);
  const natalDone = useRef(false);
  const monthlyDone = useRef(false);

  useEffect(() => {
    if (!session?.token || !cognitoId) return;

    async function pollCharts() {
      if (!natalDone.current) {
        try {
          const r = await fetch(`${baseUrl}user/customer-astro-response-details-fetch`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
            body: JSON.stringify({ astro_toolkit_tab: "western_horoscope_v2", userid: userId }),
          });
          const d = await r.json();
          const chart = d?.data ?? d?.results?.res ?? d?.results;
          if (chart?.chart_image ?? chart?.chart_url) {
            setNatalChart(chart);
            natalDone.current = true;
          }
        } catch { /* silent — will retry next interval */ }
      }

      if (!monthlyDone.current) {
        try {
          const r = await fetch(`${baseUrl}user/customer-astro-response-details-fetch`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
            body: JSON.stringify({ astro_toolkit_tab: "tropical_transits_monthly_v3", userid: userId }),
          });
          const d = await r.json();
          const chart = d?.data ?? d?.results?.res ?? d?.results;
          if (chart?.chart_image ?? chart?.chart_url) {
            setMonthlyTransit(chart);
            monthlyDone.current = true;
          }
        } catch { /* silent */ }
      }

      if (natalDone.current && monthlyDone.current) {
        setIsPollingCharts(false);
      }
    }

    void pollCharts();
    const interval = setInterval(() => {
      if (natalDone.current && monthlyDone.current) {
        clearInterval(interval);
        return;
      }
      void pollCharts();
    }, 10_000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token, cognitoId]);

  // ── Task 02 & 03: Member details (profile rings + relationship list) ───────
  const { data: memberDetailsData, isLoading: membersLoading } = useQuery<{ results?: MemberData | MemberData[]; data?: MemberData | MemberData[]; res?: MemberData[] }>({
    queryKey: ["perennial-member-details", cognitoId],
    queryFn: async () => {
      const r = await fetch(`${baseUrl}user/perennial_mandalism_aditonal_member-details-fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ user_cognito_id: cognitoId }),
      });
      return r.json();
    },
    enabled: !!session?.token && !!cognitoId,
    staleTime: 5 * 60 * 1000,
  });

  const rawMembers = memberDetailsData?.res ?? memberDetailsData?.data ?? memberDetailsData?.results;
  const memberList: MemberData[] = Array.isArray(rawMembers) ? rawMembers : rawMembers ? [rawMembers] : [];
  const mainMember = memberList[0] ?? null;

  // ── Task 05: Membership details + content counts ──────────────────────────
  const { data: membershipData, isLoading: membershipLoading } = useQuery<{
    status?: string; results?: MembershipDetails; data?: MembershipDetails;
  }>({
    queryKey: ["perennial-membership"],
    queryFn: async () => {
      const r = await fetch(`${baseUrl}user/fetch-membership-details`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ user_cognito_id: cognitoId }),
      });
      return r.json();
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });
  const membership: MembershipDetails | null = membershipData?.results ?? membershipData?.data ?? null;

  const { data: contentCountsData, isLoading: countsLoading } = useQuery<ContentCounts>({
    queryKey: ["content-type-counts"],
    queryFn: async () => {
      const r = await fetch(`${baseUrl}content/content-type-counts`, {
        headers: { Authorization: `Bearer ${session?.token}` },
      });
      return r.json();
    },
    enabled: !!session?.token,
    staleTime: 10 * 60 * 1000,
  });

  // ── Task 06: Blog list, Video list, Content table ─────────────────────────
  const listPayload = { condition: { limit: 10, skip: 0 }, searchcondition: {}, sort: { type: "desc", field: "created_on" }, project: {}, token: "" };

  const { data: blogsData, isLoading: blogsLoading } = useQuery<{ res?: BlogItem[]; results?: { res?: BlogItem[] } }>({
    queryKey: ["blogs-frontend"],
    queryFn: async () => {
      const r = await fetch(`${baseUrl}blogmanagement/list-for-front-end`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify(listPayload),
      });
      return r.json();
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });
  const blogs: BlogItem[] = blogsData?.results?.res ?? blogsData?.res ?? [];

  const { data: videosData, isLoading: videosLoading } = useQuery<{ res?: VideoItem[]; results?: { res?: VideoItem[] } }>({
    queryKey: ["videos-frontend"],
    queryFn: async () => {
      const r = await fetch(`${baseUrl}videomanagement/list-for-front-end`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify(listPayload),
      });
      return r.json();
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });
  const videos: VideoItem[] = videosData?.results?.res ?? videosData?.res ?? [];

  const { data: contentData, isLoading: contentLoading } = useQuery<{ res?: ContentItem[]; results?: { res?: ContentItem[] } }>({
    queryKey: ["content-frontend"],
    queryFn: async () => {
      const r = await fetch(`${baseUrl}content/content-list-for-frontend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ searchcondition: {}, sort: { type: "asc", field: "priority" }, project: {}, token: "" }),
      });
      return r.json();
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });
  const contentItems: ContentItem[] = contentData?.results?.res ?? contentData?.res ?? [];

  // ── Subscribe mutation ────────────────────────────────────────────────────
  const subscribeMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${baseUrl}cart_management/subscription-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({
          subscription_type: "perennial_mandalism",
          success_url: `${window.location.origin}/admin/perennial`,
          cancel_url: `${window.location.origin}/admin/perennial`,
        }),
      });
      return r.json();
    },
    onSuccess: (data) => {
      const url = data?.url ?? data?.checkout_url ?? data?.data?.url;
      if (url) window.location.href = url;
      else toast.success("Subscription initiated. Please check your email.");
    },
    onError: () => toast.error("Failed to initiate subscription. Please try again."),
  });

  return (
    <div className="space-y-6">
      {/* Page title */}
      <div className="flex items-center gap-3">
        <div className="h-7 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Perennial Mandalism Dashboard</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Your rituals, members, and perennial journey</p>
        </div>
      </div>

      {/* Subscription status */}
      <SubscriptionWidget
        membership={membership}
        isLoading={membershipLoading}
        onSubscribe={() => subscribeMutation.mutate()}
        subscribing={subscribeMutation.isPending}
      />

      {/* Task 01: Astro Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <AstroChartCard
          title="Your Natal Chart"
          tooltip="Your natal chart shows planetary positions at the moment of your birth, revealing your innate spiritual blueprint."
          chartData={natalChart}
          isPolling={isPollingCharts}
        />
        <AstroChartCard
          title="Monthly Transit"
          tooltip="Monthly transits reveal how current planetary movements influence your spiritual journey this month."
          chartData={monthlyTransit}
          isPolling={isPollingCharts}
        />
      </div>

      {/* Task 02 + 03: Profile rings + Relationship list */}
      <div className="grid gap-4 md:grid-cols-2">
        <ProfileRingsCard memberData={mainMember} isLoading={membersLoading} />
        <RelationshipCard members={memberList} isLoading={membersLoading} />
      </div>

      {/* Task 05: Content counts */}
      <ContentCountsCard counts={contentCountsData ?? null} isLoading={countsLoading} />

      {/* Quick links */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <QuickCard title="My Rituals"           description="Access your ritual invocations and planetary rituals"    icon={Flame}     href="/admin/perennial/rituals" />
        <QuickCard title="Additional Members"   description="Manage additional members in your subscription"          icon={Users}     href="/admin/perennial/members" />
        <QuickCard title="Packages"             description="View your Perennial Mandalism subscription"             icon={Package}   href="/admin/packages" />
        <QuickCard title="Training Center"      description="Access your training courses and materials"             icon={BookOpen}  href="/admin/training/center" />
        <QuickCard title="Testimonials"         description="Share your Perennial Mandalism experience"              icon={Star}      href="/admin/testimonials/create" />
        <QuickCard title="Relationship Details" description="View your member relationship details"                  icon={Heart}     href="/admin/perennial/relationship-details" />
      </div>

      {/* Task 04: Promo blocks */}
      <PromoBlocks />

      {/* Task 06: Media lists */}
      <div className="grid gap-4 md:grid-cols-2">
        <BlogListCard blogs={blogs} isLoading={blogsLoading} />
        <VideoListCard videos={videos} isLoading={videosLoading} />
      </div>
      <ContentTableCard items={contentItems} isLoading={contentLoading} />
    </div>
  );
}
